import {
  AvatarNext as Avatar,
  ButtonCircle,
  IconNext,
} from '@momentum-ui/react-collaboration';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoicemailAudio } from '../components/Voicemail/VoicemailAudioProvider';
import { IVoicemailItemProps } from '../components/Voicemail/VoicemailItem';
import { VoicemailPlaybackControls } from '../components/Voicemail/VoicemailPlaybackControls';
import { VoicemailTranscriptPanel } from '../components/Voicemail/VoicemailTranscriptPanel';
import {
  MobileAudioCallIcon,
  MobileBackIcon,
  MobileCallVoicemailIcon,
  MobileMarkUnreadIcon,
  MobilePlayIcon,
  MobileVideoCallIcon,
} from '../components/MobileActionIcons/MobileActionIcons';
import { removeBracketsAndContent } from '../utils/avatarInitials';
import {
  formatDate,
  formatDateDDMMYYYY,
  formatTime,
  formatTimeToSupport24Hours,
} from '../utils/dateUtils';
import './MobileVoicemail.styles.scss';

type MobileVoicemailFilter = 'all' | 'unread';
type TranscriptRequestGuard = {
  voicemailId: string;
  request: () => void;
};
const MAX_RENDERED_VOICEMAIL_ITEMS = 200;

export interface IMobileVoicemailProps {
  items: IVoicemailItemProps[];
  isLocaleGerman?: boolean;
  fetchNextBatch?: () => Promise<unknown> | void;
  hasMoreItems?: boolean;
  onDetailOpenChange?: (isOpen: boolean) => void;
  onCallVoicemail?: () => void;
}

export const MobileVoicemail = ({
  items,
  isLocaleGerman = false,
  fetchNextBatch = undefined,
  hasMoreItems = true,
  onDetailOpenChange,
  onCallVoicemail,
}: IMobileVoicemailProps): JSX.Element => {
  const { t } = useTranslation('WebexVoicemail');
  const audioContext = useVoicemailAudio();
  const [filter, setFilter] = useState<MobileVoicemailFilter>('all');
  const [selectedId, setSelectedId] = useState<string>();
  const [isAnnouncePlayOrPause, setIsAnnouncePlayOrPause] = useState(false);
  const restoreFocusId = useRef<string>();
  const lastRestoredFocusId = useRef<string>();
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const allFilterRef = useRef<HTMLButtonElement>(null);
  const unreadFilterRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const pauseButtonRef = useRef<HTMLButtonElement>(null);
  const nextAfterScrubberRef = useRef<HTMLButtonElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLElement>(null);
  const isFetchingNextBatch = useRef(false);
  const wasInDetail = useRef(false);
  const requestedTranscript = useRef<TranscriptRequestGuard>();

  const filteredItems = useMemo(
    () =>
      filter === 'unread'
        ? items.filter(({ voicemail }) => voicemail.unread)
        : items,
    [filter, items]
  );
  const selectedItem = items.find(
    ({ voicemail }) => voicemail.id === selectedId
  );
  useEffect(() => {
    onDetailOpenChange?.(!!selectedItem);
  }, [onDetailOpenChange, selectedItem]);
  const visibleFilteredItems = useMemo(
    () => filteredItems.slice(0, MAX_RENDERED_VOICEMAIL_ITEMS),
    [filteredItems]
  );
  const canFetchMoreItems = hasMoreItems && items.length < MAX_RENDERED_VOICEMAIL_ITEMS;

  useEffect(() => {
    if (!selectedId) {
      requestedTranscript.current = undefined;
      return;
    }
    const request = selectedItem?.onRequestTranscript;
    const alreadyRequested =
      requestedTranscript.current?.voicemailId === selectedId &&
      requestedTranscript.current.request === request;
    if (
      selectedItem?.transcriptEnabled &&
      !selectedItem.transcriptLoading &&
      (!selectedItem.transcript || selectedItem.transcriptKind === 'status') &&
      request &&
      !alreadyRequested
    ) {
      requestedTranscript.current = { voicemailId: selectedId, request };
      request();
    }
  }, [
    selectedId,
    selectedItem?.onRequestTranscript,
    selectedItem?.transcript,
    selectedItem?.transcriptKind,
    selectedItem?.transcriptEnabled,
    selectedItem?.transcriptLoading,
  ]);

  const triggerFetchNextBatch = useCallback(() => {
    if (!fetchNextBatch || isFetchingNextBatch.current || !canFetchMoreItems) {
      return;
    }
    isFetchingNextBatch.current = true;
    Promise.resolve()
      .then(fetchNextBatch)
      .catch(() => undefined)
      .finally(() => {
        isFetchingNextBatch.current = false;
      });
  }, [canFetchMoreItems, fetchNextBatch]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || !canFetchMoreItems || !fetchNextBatch || selectedItem) {
      return;
    }
    if (container.scrollHeight <= container.clientHeight + 1) {
      triggerFetchNextBatch();
    }
  }, [
    fetchNextBatch,
    filteredItems.length,
    items.length,
    canFetchMoreItems,
    selectedItem,
    triggerFetchNextBatch,
  ]);

  const focusActiveFilter = useCallback(() => {
    const filterRef = filter === 'unread' ? unreadFilterRef : allFilterRef;
    filterRef.current?.focus();
  }, [filter]);

  useEffect(() => {
    if (selectedItem) {
      if (!wasInDetail.current) {
        backButtonRef.current?.focus();
      }
      wasInDetail.current = true;
      return;
    }

    if (selectedId) {
      setSelectedId(undefined);
      return;
    }

    wasInDetail.current = false;

    if (restoreFocusId.current) {
      const row = rowRefs.current.get(restoreFocusId.current);
      if (row) {
        row.focus();
        lastRestoredFocusId.current = restoreFocusId.current;
      } else {
        focusActiveFilter();
      }
      restoreFocusId.current = undefined;
      return;
    }

    if (
      lastRestoredFocusId.current &&
      !filteredItems.some(
        ({ voicemail }) => voicemail.id === lastRestoredFocusId.current
      )
    ) {
      focusActiveFilter();
      lastRestoredFocusId.current = undefined;
    }
  }, [
    filteredItems,
    focusActiveFilter,
    selectedId,
    selectedItem,
  ]);

  if (selectedItem) {
    const { voicemail } = selectedItem;
    const closeDetails = () => {
      audioContext.stopVoicemail();
      setSelectedId(undefined);
    };

    return (
      <section
        className="mobile-voicemail mobile-voicemail--detail"
        data-testid="mobile-voicemail-detail"
      >
        <header className="mobile-voicemail__detail-header">
          <button
            type="button"
            ref={backButtonRef}
            className="mobile-voicemail__back"
            aria-label={t('mobile.backToVoicemail', {
              defaultValue: 'Back to voicemail',
            })}
            onClick={closeDetails}
          >
            <MobileBackIcon size={24} />
          </button>
          <h2>
            {t('mobile.voicemailInformation', {
              defaultValue: 'Voicemail information',
            })}
          </h2>
        </header>

        <div className="mobile-voicemail__identity">
          <Avatar
            initials={removeBracketsAndContent(
              voicemail.name || voicemail.address
            )}
            title={voicemail.name}
            size={72}
          />
          <h3>{voicemail.name || voicemail.address}</h3>
          {voicemail.address && <p>{voicemail.address}</p>}
        </div>

        <div
          className="mobile-voicemail__detail-actions"
          aria-label={t('mobile.voicemailActions', {
            defaultValue: 'Voicemail actions',
          })}
        >
          <div>
            <ButtonCircle
              size={40}
              color="join"
              onPress={selectedItem.useMakeAudioCall}
              disabled={!voicemail.address}
              aria-label={t('mobile.audio', { defaultValue: 'Audio' })}
            >
              <MobileAudioCallIcon size={20} />
            </ButtonCircle>
            <span>{t('mobile.audio', { defaultValue: 'Audio' })}</span>
          </div>
          <div>
            <ButtonCircle
              size={40}
              color="join"
              onPress={selectedItem.useMakeVideoCall}
              disabled={!voicemail.address}
              aria-label={t('mobile.video', { defaultValue: 'Video' })}
            >
              <MobileVideoCallIcon size={20} />
            </ButtonCircle>
            <span>{t('mobile.video', { defaultValue: 'Video' })}</span>
          </div>
        </div>

        <div className="mobile-voicemail__player">
          <div className="mobile-voicemail__player-date">
            <span>
              {isLocaleGerman
                ? formatDateDDMMYYYY(voicemail.date)
                : formatDate(voicemail.date)}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {isLocaleGerman
                ? formatTimeToSupport24Hours(voicemail.date)
                : formatTime(voicemail.date)}
            </span>
          </div>
          <div className="mobile-voicemail__player-body">
            <div
              className="mobile-voicemail__playback"
              aria-label={
                isAnnouncePlayOrPause
                  ? t('pauseVoicemail')
                  : t('playVoicemail')
              }
            >
              <VoicemailPlaybackControls
                voicemailId={voicemail.id}
                audioSrc={selectedItem.voicemailSrc}
                audioSrcLoader={selectedItem.voicemailSrcLoader}
                duration={voicemail.duration}
                onPlay={selectedItem.onRead}
                setIsAnnouncePlayOrPause={setIsAnnouncePlayOrPause}
                voicemailName={voicemail.name}
                playButtonRef={playButtonRef}
                pauseButtonRef={pauseButtonRef}
                audioButtonRef={nextAfterScrubberRef}
                trackRef={trackRef}
                isSelected
                buttonSize={52}
                playIcon={<MobilePlayIcon size={28} />}
                compactTime
                setAccordionExpanded={() => undefined}
              />
            </div>

            <div className="mobile-voicemail__player-tools">
              <div>
                <ButtonCircle
                  size={52}
                  outline
                  ghost
                  ref={nextAfterScrubberRef}
                  onPress={() =>
                    voicemail.unread
                      ? selectedItem.onMarkRead?.()
                      : selectedItem.onUnread?.()
                  }
                  aria-label={
                    voicemail.unread
                      ? t('mobile.markAsRead', { defaultValue: 'Mark as read' })
                      : t('mobile.markAsUnread', {
                          defaultValue: 'Mark as unread',
                        })
                  }
                  data-testid={
                    voicemail.unread ? 'mobile-mark-read' : 'mobile-mark-unread'
                  }
                >
                  <MobileMarkUnreadIcon size={25} />
                </ButtonCircle>
                <span>
                  {voicemail.unread
                    ? t('mobile.markAsRead', { defaultValue: 'Mark as read' })
                    : t('mobile.markAsUnread', {
                        defaultValue: 'Mark as unread',
                      })}
                </span>
              </div>
              <div>
                <ButtonCircle
                  className="mobile-voicemail__delete"
                  size={52}
                  outline
                  ghost
                  onPress={() => {
                    audioContext.stopVoicemail();
                    selectedItem.onDelete?.();
                    setSelectedId(undefined);
                  }}
                  aria-label={t('deleteVoicemail')}
                >
                  <IconNext name="delete" scale={22} />
                </ButtonCircle>
                <span>{t('deleteVoicemail')}</span>
              </div>
            </div>
          </div>
        </div>

        {selectedItem.transcriptEnabled && (
          <VoicemailTranscriptPanel
            transcript={selectedItem.transcript}
            transcriptKind={selectedItem.transcriptKind}
            loading={Boolean(selectedItem.transcriptLoading)}
            date={voicemail.date}
            isLocaleGerman={isLocaleGerman}
            headingLevel={3}
            collapsible
          />
        )}
      </section>
    );
  }

  return (
    <section
      ref={listContainerRef}
      className="mobile-voicemail"
      data-testid="mobile-voicemail"
      onScroll={(event) => {
        const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
        const isNearEnd = scrollHeight - scrollTop - clientHeight <= 48;
        if (isNearEnd) {
          triggerFetchNextBatch();
        }
      }}
    >
      <div className="mobile-voicemail__filters">
        <div
          role="group"
          aria-label={t('mobile.voicemailFilters', {
            defaultValue: 'Voicemail filters',
          })}
        >
          <button
            type="button"
            ref={allFilterRef}
            aria-pressed={filter === 'all'}
            onClick={() => {
              lastRestoredFocusId.current = undefined;
              setFilter('all');
            }}
          >
            {t('mobile.all', { defaultValue: 'All' })}
          </button>
          <button
            type="button"
            ref={unreadFilterRef}
            aria-pressed={filter === 'unread'}
            onClick={() => {
              lastRestoredFocusId.current = undefined;
              setFilter('unread');
            }}
          >
            {t('mobile.unread', { defaultValue: 'Unread' })}
          </button>
        </div>
        {onCallVoicemail && (
          <div className="mobile-voicemail__call-voicemail">
            <ButtonCircle
              ghost
              size={40}
              aria-label={t('callVoicemail', { defaultValue: 'Call voicemail' })}
              onPress={onCallVoicemail}
              data-testid="mobile-call-voicemail"
            >
              <MobileCallVoicemailIcon size={28} />
            </ButtonCircle>
          </div>
        )}
      </div>

      {visibleFilteredItems.length === 0 && (
        <p className="mobile-voicemail__filter-empty" role="status">
          {t(
            filter === 'unread'
              ? 'mobile.noUnreadVoicemails'
              : 'mobile.noVoicemails',
            {
              defaultValue:
                filter === 'unread' ? 'No unread voicemails' : 'No voicemails',
            }
          )}
        </p>
      )}

      <ul className="mobile-voicemail__list">
        {visibleFilteredItems.map((item) => {
          const { voicemail } = item;
          return (
            <li key={voicemail.id}>
              <button
                type="button"
                ref={(element) => {
                  if (element) {
                    rowRefs.current.set(voicemail.id, element);
                  } else {
                    rowRefs.current.delete(voicemail.id);
                  }
                }}
                className="mobile-voicemail__row"
                aria-label={`${
                  voicemail.unread ? `${t('unreadVoicemail')}. ` : ''
                }${t('mobile.openVoicemailDetails', {
                  defaultValue: `Open voicemail details for ${
                    voicemail.name || voicemail.address
                  }`,
                  name: voicemail.name || voicemail.address,
                })}`}
                onClick={() => {
                  lastRestoredFocusId.current = undefined;
                  restoreFocusId.current = voicemail.id;
                  setSelectedId(voicemail.id);
                }}
              >
                <span className="mobile-voicemail__avatar-wrap">
                  <Avatar
                    initials={removeBracketsAndContent(
                      voicemail.name || voicemail.address
                    )}
                    title={voicemail.name}
                    size={48}
                  />
                  {voicemail.unread && (
                    <span
                      className="mobile-voicemail__unread-dot"
                      role="img"
                      aria-label={t('unreadVoicemail')}
                    />
                  )}
                </span>
                <span className="mobile-voicemail__row-content">
                  <strong className="mobile-voicemail__row-name">
                    {voicemail.name || voicemail.address}
                  </strong>
                  <span className="mobile-voicemail__row-metadata">
                    {voicemail.address}
                  </span>
                </span>
                <span className="mobile-voicemail__row-time">
                  <span className="mobile-voicemail__row-date">
                    {isLocaleGerman
                      ? formatDateDDMMYYYY(voicemail.date)
                      : formatDate(voicemail.date)}
                  </span>
                  <span className="mobile-voicemail__row-clock">
                    {isLocaleGerman
                      ? formatTimeToSupport24Hours(voicemail.date)
                      : formatTime(voicemail.date)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
